import { useEffect, useState } from "react";

// Layout Constants in pixels
export const NAVBAR_HEIGHT = 90;
export const FOOTER_HEIGHT = 50;
export const SIDEBAR_WIDTH = 260;
export const DOCUMENT_PADDING = 16;

// Screen size breakpoints (in pixels) - using Tailwind config breakpoints
const SCREEN_SIZES = {
  SMALL: 576, // sm
  MEDIUM: 768, // md
  LARGE: 992, // lg
  EXTRA_LARGE: 1200, // xl
  TWO_XL: 1320, // 2xl
  THREE_XL: 1600, // 3xl
  FOUR_XL: 1850, // 4xl
} as const;

// Utility functions to check screen sizes
const isSmallScreen = (width: number): boolean => width < SCREEN_SIZES.MEDIUM;
const isMediumScreen = (width: number): boolean =>
  width >= SCREEN_SIZES.MEDIUM && width < SCREEN_SIZES.LARGE;
const isLargeScreen = (width: number): boolean =>
  width >= SCREEN_SIZES.LARGE && width < SCREEN_SIZES.EXTRA_LARGE;
const isExtraLargeScreen = (width: number): boolean =>
  width >= SCREEN_SIZES.EXTRA_LARGE && width < SCREEN_SIZES.TWO_XL;
const isTwoXLScreen = (width: number): boolean =>
  width >= SCREEN_SIZES.TWO_XL && width < SCREEN_SIZES.THREE_XL;
const isThreeXLScreen = (width: number): boolean =>
  width >= SCREEN_SIZES.THREE_XL && width < SCREEN_SIZES.FOUR_XL;
const isFourXLScreen = (width: number): boolean =>
  width >= SCREEN_SIZES.FOUR_XL;

// Utility functions to check if screen size is greater than breakpoints
const isGreaterThanSmall = (width: number): boolean =>
  width >= SCREEN_SIZES.MEDIUM;
const isGreaterThanMedium = (width: number): boolean =>
  width >= SCREEN_SIZES.LARGE;
const isGreaterThanLarge = (width: number): boolean =>
  width >= SCREEN_SIZES.EXTRA_LARGE;
const isGreaterThanExtraLarge = (width: number): boolean =>
  width >= SCREEN_SIZES.TWO_XL;
const isGreaterThanTwoXL = (width: number): boolean =>
  width >= SCREEN_SIZES.THREE_XL;
const isGreaterThanThreeXL = (width: number): boolean =>
  width >= SCREEN_SIZES.FOUR_XL;

// Helper function to get current screen size category
const getScreenSize = (
  width: number
): "small" | "medium" | "large" | "extra-large" | "2xl" | "3xl" | "4xl" => {
  if (isSmallScreen(width)) return "small";
  if (isMediumScreen(width)) return "medium";
  if (isLargeScreen(width)) return "large";
  if (isExtraLargeScreen(width)) return "extra-large";
  if (isTwoXLScreen(width)) return "2xl";
  if (isThreeXLScreen(width)) return "3xl";
  return "4xl";
};

const getNavHeightPlusMargins = () => {
  return NAVBAR_HEIGHT + 2 * DOCUMENT_PADDING;
};

const getControlHeight = (refOrId: HTMLDivElement | string): number => {
  const control =
    typeof refOrId === "string" ? document.getElementById(refOrId) : refOrId;

  if (!control) return 0;

  const style = window.getComputedStyle(control);
  if (style.display === "none") return 0; // Handle hidden elements

  return control.clientHeight;
};

const getContentHeight = () => {
  return window.innerHeight - getNavHeightPlusMargins() - FOOTER_HEIGHT;
};

const useViewportDimensions = () => {
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    navHeight: getNavHeightPlusMargins(),
    contentHeight: getContentHeight(),
  });

  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
        navHeight: getNavHeightPlusMargins(),
        contentHeight: getContentHeight(),
      });
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return {
    dimensions,
    getControlHeight,
    isSmallScreen: isSmallScreen(dimensions.width),
    isMediumScreen: isMediumScreen(dimensions.width),
    isLargeScreen: isLargeScreen(dimensions.width),
    isExtraLargeScreen: isExtraLargeScreen(dimensions.width),
    isTwoXLScreen: isTwoXLScreen(dimensions.width),
    isThreeXLScreen: isThreeXLScreen(dimensions.width),
    isFourXLScreen: isFourXLScreen(dimensions.width),
    isGreaterThanSmall: isGreaterThanSmall(dimensions.width),
    isGreaterThanMedium: isGreaterThanMedium(dimensions.width),
    isGreaterThanLarge: isGreaterThanLarge(dimensions.width),
    isGreaterThanExtraLarge: isGreaterThanExtraLarge(dimensions.width),
    isGreaterThanTwoXL: isGreaterThanTwoXL(dimensions.width),
    isGreaterThanThreeXL: isGreaterThanThreeXL(dimensions.width),
    screenSize: getScreenSize(dimensions.width),
  };
};

export const ContentHeightLayout = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { dimensions } = useViewportDimensions();
  return (
    <div
      className="absolute w-full overflow-y-auto"
      style={{
        height: dimensions.contentHeight,
        top: NAVBAR_HEIGHT + 2 * DOCUMENT_PADDING,
      }}
    >
      {children}
    </div>
  );
};

export const TableHeightLayout = ({
  offset,
  children,
}: {
  children: React.ReactNode;
  offset?: number;
}) => {
  const { dimensions } = useViewportDimensions();
  return (
    <div
      className="absolute w-full"
      style={{
        height: dimensions.contentHeight - (offset || 0),
      }}
    >
      {children}
    </div>
  );
};

export const SecondaryHeightLayout = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { dimensions } = useViewportDimensions();
  return (
    <div
      className="absolute w-full px-2 md:px-4"
      style={{
        height: dimensions.contentHeight,
      }}
    >
      {children}
    </div>
  );
};

export default useViewportDimensions;
